import classNames from 'classnames'
import { type PropsWithChildren, useRef, useState } from 'react'
import { type IIconsVector, Icon, Tooltip, Typography, type ITooltipProps } from 'shared/ui'

import styles from './styles.module.scss'
import { ChatbotTooltip } from '../ChatbotTooltip'

type IChatbotCollapseProps = PropsWithChildren<{
  icon: IIconsVector
  title: string
  tooltipProps?: ITooltipProps
  error?: string | null
  onChange?: (expanded: boolean) => void
  isShowBottomBorder?: boolean
}>

export const ChatbotCollapse = ({
  icon,
  title,
  tooltipProps,
  error,
  children,
  onChange,
  isShowBottomBorder = true,
}: IChatbotCollapseProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLElement | null>(null)
  const [expanded, expand] = useState(false)

  const handleClick = () => {
    const isExpanded = !expanded

    expand(isExpanded)
    onChange?.(isExpanded)
  }

  return (
    <section
      className={classNames(styles.container, {
        [styles.expanded]: expanded,
      })}
    >
      <header
        ref={ref}
        className={styles.header}
        onClick={handleClick}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <span className={styles.title}>
          <span className={styles.icon}>
            <Icon icon={icon} fontSize={16} />
          </span>
          <Typography className={styles.text} variant='body-lg-medium' ariaLabel={'title'}>
            {title} {!!tooltipProps && <ChatbotTooltip tooltipProps={tooltipProps} />}
          </Typography>
          {!!error && (
            <Tooltip
              color='red'
              placement='bottom'
              open={open}
              label={error}
              breakWord
              maxWidth={360}
            >
              <Icon icon='alertTriangle' fontSize={16} color={'var(--content-negative-primary)'} />
            </Tooltip>
          )}
        </span>

        <Icon
          fontSize={16}
          color={'var(--content-primary-tertiary)'}
          icon={expanded ? 'chevronUp' : 'chevronDown'}
        />
      </header>
      {expanded && (
        <div className={classNames(styles.content, { [styles.bottomBorder]: isShowBottomBorder })}>
          {children}
        </div>
      )}
    </section>
  )
}
